import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useListServersQuery } from '../store/api';
import {
  UnorderedListOutlined,
  DashboardOutlined,
  UserOutlined,
  ProjectOutlined,
  LogoutOutlined,
  LineChartOutlined,
  FundOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  AppstoreOutlined,
  DatabaseOutlined,
  LinkOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  AlertOutlined,
  BookOutlined,
  FileTextOutlined,
  CloudOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  RobotOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import dayjs from 'dayjs';

const navItems = [
  { type: 'section', label: 'MAIN' },
  { key: 'dashboard', icon: <DashboardOutlined />, label: 'Dashboard', path: '/dashboard', badge: null },
  { key: 'lista', icon: <UnorderedListOutlined />, label: 'Servidores', path: '/lista', badgeKey: 'servers' },
  { key: 'metricas', icon: <FundOutlined />, label: 'Métricas', path: '/metricas' },
  { type: 'section', label: 'GESTIÓN' },
  { key: 'clientes', icon: <UserOutlined />, label: 'Clientes', path: '/clientes' },
  { key: 'proyectos', icon: <ProjectOutlined />, label: 'Proyectos', path: '/proyectos' },
  { key: 'proyecto-infra', icon: <CloudOutlined />, label: 'Proyecto / Infra', path: '/proyecto' },
  { key: 'servicios', icon: <AppstoreOutlined />, label: 'Servicios', path: '/servicios' },
  { type: 'section', label: 'MONITOREO' },
  { key: 'url-monitor', icon: <LinkOutlined />, label: 'Monitor URLs', path: '/url-monitor' },
  { key: 'ssl-monitor', icon: <SafetyCertificateOutlined />, label: 'Monitor SSL', path: '/ssl-monitor' },
  { key: 'dominios', icon: <GlobalOutlined />, label: 'Dominios', path: '/dominios' },
  { type: 'section', label: 'OPERACIONES' },
  { key: 'alertas', icon: <AlertOutlined />, label: 'Alertas', path: '/alertas' },
  { key: 'bitacora', icon: <BookOutlined />, label: 'Bitácora', path: '/bitacora' },
  { key: 'reportes', icon: <FileTextOutlined />, label: 'Reportes', path: '/reportes' },
  { key: 'procesos', icon: <ClockCircleOutlined />, label: 'Procesos', path: '/procesos' },
  { type: 'section', label: 'ANÁLISIS' },
  { key: 'historial', icon: <LineChartOutlined />, label: 'Historial Cargas', path: '/historial' },
  { key: 'mysql', icon: <DatabaseOutlined />, label: 'MySQL Monitor', path: '/mysql' },
  { key: 'infraestructura', icon: <CloudOutlined />, label: 'Infraestructura', path: '/infraestructura' },
  { key: 'mapa-infra', icon: <ApartmentOutlined />, label: 'Mapa Infra', path: '/mapa-infra' },
  { type: 'section', label: 'IA SMART' },
  { key: 'ollama', icon: <RobotOutlined />, label: 'Ollama Monitor', path: '/ollama' },
  { type: 'section', label: 'CONFIGURACIÓN' },
  { key: 'admin-smtp', icon: <SettingOutlined />, label: 'SMTP & 2FA', path: '/admin/smtp' },
];

const Sidebar = ({ collapsed, onCollapse, mobileOpen, onMobileClose, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (path) => {
    navigate(path);
    if (onMobileClose) onMobileClose();
  };

  const handleLogout = () => {
    if (onMobileClose) onMobileClose();
    onLogout();
  };

  // Get server count for badge
  const { data: servers } = useListServersQuery(undefined, { pollingInterval: 60000 });
  const alertCount = useSelector((state) => {
    // We'll compute alerts from servers
    if (!servers) return 0;
    const now = dayjs();
    const sinReporte = servers.filter(s => {
      if (!s.fechaupdatedsincro) return true;
      return now.diff(dayjs(s.fechaupdatedsincro), 'minute') > 10;
    }).length;
    const cargaAlta = servers.filter(s => {
      const c = parseFloat((s.carga || '').split(',')[0]);
      return c > (s.limitealerta || 1.5);
    }).length;
    return sinReporte + cargaAlta;
  });

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return 'dashboard';
    if (path === '/lista') return 'lista';
    if (path === '/clientes') return 'clientes';
    if (path === '/proyectos') return 'proyectos';
    if (path === '/proyecto') return 'proyecto-infra';
    if (path === '/servicios') return 'servicios';
    if (path === '/historial') return 'historial';
    if (path === '/metricas') return 'metricas';
    if (path === '/mysql') return 'mysql';
    if (path === '/url-monitor') return 'url-monitor';
    if (path === '/ssl-monitor') return 'ssl-monitor';
    if (path === '/dominios') return 'dominios';
    if (path === '/alertas') return 'alertas';
    if (path === '/bitacora') return 'bitacora';
    if (path === '/reportes') return 'reportes';
    if (path === '/infraestructura') return 'infraestructura';
    if (path === '/mapa-infra') return 'mapa-infra';
    if (path === '/ollama') return 'ollama';
    if (path === '/admin/smtp') return 'admin-smtp';
    if (path.startsWith('/proyecto/')) return 'proyectos';
    return 'dashboard';
  };

  const selectedKey = getSelectedKey();

  const getBadge = (item) => {
    if (item.badgeKey === 'servers' && servers) return servers.length;
    if (item.key === 'dashboard' && alertCount > 0) return alertCount;
    return null;
  };

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      {/* Brand */}
      <div className="sidebar-brand">
        <div className="sidebar-brand-icon">
          <img src="/logo.png" alt="SmartAdmin" style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </div>
        <div className="sidebar-brand-text">
          <div className="sidebar-brand-name">SmartAdmin</div>
          <div className="sidebar-brand-subtitle">Server Monitor</div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {navItems.map((item, index) => {
          if (item.type === 'section') {
            return (
              <div key={`section-${index}`} className="sidebar-section-title">
                {item.label}
              </div>
            );
          }

          const badge = getBadge(item);

          const btn = (
            <button
              key={item.key}
              className={`sidebar-nav-item ${selectedKey === item.key ? 'active' : ''}`}
              onClick={() => handleNavigate(item.path)}
            >
              {item.icon}
              <span className="sidebar-nav-item-label">{item.label}</span>
              {badge !== null && <span className="sidebar-badge">{badge}</span>}
            </button>
          );

          return collapsed ? (
            <Tooltip key={item.key} title={item.label} placement="right">
              {btn}
            </Tooltip>
          ) : (
            <React.Fragment key={item.key}>{btn}</React.Fragment>
          );
        })}
      </nav>

      {/* Logout */}
      <div style={{ padding: '8px 12px' }}>
        <Tooltip title={collapsed ? 'Cerrar Sesión' : ''} placement="right">
          <button className="sidebar-nav-item danger" onClick={handleLogout}>
            <LogoutOutlined />
            <span className="sidebar-nav-item-label">Cerrar Sesión</span>
          </button>
        </Tooltip>
      </div>

      {/* Toggle */}
      <div className="sidebar-toggle">
        <button className="sidebar-toggle-btn" onClick={() => onCollapse(!collapsed)}>
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
